import { Fragment, ReactNode, useMemo, useState } from 'react';
import { createSafeContext } from '@mews/framework';
import { createUUID } from '@mews/std';

interface DialogContextProps {
    openDialog: (dialog: ReactNode, id?: string) => void;
    closeDialog: (id?: string) => void;
    closeDialogs: () => void;
}

export interface DialogProviderProps {
    children: ReactNode;
}

interface DialogsStackItem {
    dialog: ReactNode;
    id: string;
}

type DialogsStack = DialogsStackItem[];

const { DialogContextProvider, useDialogContext } = createSafeContext<'DialogContext', DialogContextProps>(
    'DialogContext'
);
/**
 * @deprecated use `useDialog` from `@mews/framework`.
 */
export const useDialogs = useDialogContext;

export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
    const [dialogs, setDialogs] = useState<DialogsStack>([]);

    const value = useMemo<DialogContextProps>(
        () => ({
            closeDialog: (id) => {
                setDialogs((state) => {
                    if (!id) {
                        return state.slice(0, state.length - 1);
                    }

                    const index = state.findIndex((item) => item.id === id);
                    return index !== -1 ? state.slice(0, index) : state;
                });
            },
            closeDialogs: () => {
                setDialogs([]);
            },
            openDialog: (dialog, id) => {
                const dialogId = id || createUUID();

                setDialogs((state) => {
                    const existingId = state.find((item) => item.id === dialogId);
                    return existingId ? state : [...state, { dialog, id: dialogId }];
                });
            },
        }),
        []
    );

    return (
        <DialogContextProvider value={value}>
            {children}
            {dialogs.map(({ dialog, id }) => (
                <Fragment key={id}>{dialog}</Fragment>
            ))}
        </DialogContextProvider>
    );
};
